'use client'

import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import { useMapDispatch } from 'maps/hooks/useMapDispatch/useMapDispatch'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import type { Location, Store } from 'maps/types'
import { sendSearchStoreNotFoundEvent } from 'maps/utils/analytics/events'
import {
	filterAndSortByNearbyStore,
	refreshDistance,
} from 'maps/utils/distance/distance'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'

const filterByBrand = (stores: Store[], brand: string): Store[] => {
	const filteredStores = stores.filter((store) =>
		store.collections.includes(brand)
	)

	return filteredStores.length > 0 ? filteredStores : stores
}

export function useFilteredStores() {
	const { hasDistanceLimit, stores = [], location, fetchStores } = useMapState()
	const dispatch = useMapDispatch()
	const { setExpanded } = useExpandedStore()
	const {
		country: { countryISO },
	} = useMasterData()

	const handleFilteredStores = async (newLocation: Location | null) => {
		if (!newLocation) {
			dispatch({ filteredStores: [], loading: false, location: newLocation })
			return
		}

		const newStores = fetchStores
			? await fetchStores(newLocation, countryISO)
			: stores

		const newFilteredStores = filterAndSortByNearbyStore(
			refreshDistance(newStores, newLocation),
			hasDistanceLimit
		)

		/** Expand first filtered store when the location changes */
		setExpanded(newFilteredStores[0], newFilteredStores)
		dispatch({
			location: newLocation,
			hasSearched: true,
			loading: false,
			stores: newStores,
			filteredStores: newFilteredStores,
		})

		if (newFilteredStores.length === 0) {
			sendSearchStoreNotFoundEvent()
		}
	}

	const handleFilteredStoresByBrand = (brand: string) => {
		const newFilteredStores = location
			? filterAndSortByNearbyStore(
					refreshDistance(stores, location),
					hasDistanceLimit
				)
			: stores

		const filteredStoresByBrand = filterByBrand(newFilteredStores, brand)

		/** Expand first filtered store when selected brand changes */
		setExpanded(filteredStoresByBrand[0], filteredStoresByBrand)
		dispatch({ selectedBrand: brand, filteredStores: filteredStoresByBrand })
	}

	return {
		handleFilteredStores,
		handleFilteredStoresByBrand,
	}
}
